/**
 * Role Constants Configuration (Frontend)
 * 
 * This file mirrors the backend role constants.
 * Keep in sync with backend/config/roles.js
 */

export const ROLES = {
  // Administrative Roles
  SUPER_ADMIN: 'SUPER_ADMIN',
  SPECTATOR: 'SPECTATOR',
  
  // Operational Roles
  ACCOUNTABLE_MANAGER: 'ACCOUNTABLE_MANAGER',
  OPERATIONS_MANAGER: 'OPERATIONS_MANAGER',
  OPERATIONS_EXECUTIVE: 'OPERATIONS_EXECUTIVE',
  SALES: 'SALES',
  PLANNING_MANAGER: 'PLANNING_MANAGER',
  PLANNING_OFFICER: 'PLANNING_OFFICER',
  
  // Flight Operations
  CREW: 'CREW',
  /** Loan pilot — same permissions and gates as company CREW (roster + final onboarding) */
  LOAN_PILOT: 'LOAN_PILOT',
  /** External / contract pilot — assignable to trips; no rostering or final onboarding gate */
  EXTERNAL_PILOT: 'EXTERNAL_PILOT',
  
  // Engineering & Maintenance
  CA_MANAGER: 'CA_MANAGER',
  CA_TECHNICAL_OFFICER: 'CA_TECHNICAL_OFFICER',
  CAMO_QM_MANAGER: 'CAMO_QM_MANAGER',
  CAMO_QM_OFFICER: 'CAMO_QM_OFFICER',
  MRO_MANAGER: 'MRO_MANAGER',
  MRO_ENGINEER: 'MRO_ENGINEER',
  MRO_QM_MANAGER: 'MRO_QM_MANAGER',
  MRO_QM_OFFICER: 'MRO_QM_OFFICER',
  /** Accountable Manager (MRO): full MRO, CA Technical Officer in CAMO, AM-level in Planning/Store/Procurement */
  AM_MRO: 'AM_MRO',
  STORE_INSPECTOR: 'STORE_INSPECTOR',
  STORE_EXECUTIVE: 'STORE_EXECUTIVE',

  // Safety Department
  SAFETY_COFS: 'SAFETY_COFS',
  SAFETY_MANAGER: 'SAFETY_MANAGER',
  SAFETY_OFFICER: 'SAFETY_OFFICER',
  
  // Reporting
  MIS: 'MIS',

  // HRMS suite (ground staff)
  HR_MANAGER: 'HR_MANAGER',

  // Generic ground employee — login + personal profile only (no module access)
  EMPLOYEE: 'EMPLOYEE',

  // External client portal (aircraft owner/operator view)
  CLIENT: 'CLIENT',

  // Hangar external operator portal (submit jobs + monitor pipeline)
  EXTERNAL_OPERATOR: 'EXTERNAL_OPERATOR'
};

// Array of all roles (for dropdowns, validation)
export const ALL_ROLES = Object.values(ROLES);

// Roles hidden from user management UI and assignment (still valid in DB for legacy users)
export const HIDDEN_ROLES = [];
export const VISIBLE_ROLES = ALL_ROLES.filter((role) => !HIDDEN_ROLES.includes(role));

/** CA Technical Officer CAMO page access; PLANNING_MANAGER and CAMO QM have parity */
export const CA_TECHNICAL_OFFICER_ACCESS = [
  ROLES.CA_TECHNICAL_OFFICER,
  ROLES.CAMO_QM_MANAGER,
  ROLES.CAMO_QM_OFFICER,
  ROLES.PLANNING_MANAGER
];

/** EDR inbox on Maintenance & Inspections: all CAMO roles + Planning Manager */
export const EDR_INBOX_ROLES = [
  ROLES.SUPER_ADMIN,
  ROLES.ACCOUNTABLE_MANAGER,
  ROLES.CA_MANAGER,
  ROLES.CA_TECHNICAL_OFFICER,
  ROLES.CAMO_QM_MANAGER,
  ROLES.CAMO_QM_OFFICER,
  ROLES.PLANNING_MANAGER
];

/**
 * Roles AM_MRO inherits via roleMatches: full MRO suite and Planning / Store
 * (procurement) module access. No CAMO Technical Officer powers.
 */
export const AM_MRO_INHERITED_ROLES = [
  ROLES.MRO_MANAGER,
  ROLES.MRO_ENGINEER,
  ROLES.MRO_QM_MANAGER,
  ROLES.MRO_QM_OFFICER,
  ROLES.PLANNING_MANAGER,
  ROLES.PLANNING_OFFICER,
  ROLES.STORE_INSPECTOR,
  ROLES.STORE_EXECUTIVE
];

/** Shown when AM MRO attempts a second consecutive workflow approval/task */
export const AM_MRO_CONSECUTIVE_WARNING =
  'AM MRO cannot perform two consecutive workflow approvals or tasks on the same item. Another authorised user must complete this step.';

/** AM-level override in MRO, Planning, Store, and Procurement (not full org AM) */
export const AM_MRO_DOMAIN_OVERRIDE_ROLES = [
  ROLES.ACCOUNTABLE_MANAGER,
  ROLES.AM_MRO,
  ROLES.SUPER_ADMIN
];

export const hasCaTechnicalOfficerAccess = (role) =>
  CA_TECHNICAL_OFFICER_ACCESS.includes(role);

/** True when role has Accountable Manager–level authority in MRO / Planning / Store / Procurement */
export const isAmMroDomainOverride = (role) =>
  AM_MRO_DOMAIN_OVERRIDE_ROLES.includes(role);

/** Company CREW or loan pilot — duty roster + final onboarding gate */
export const ROSTER_CREW_ROLES = [ROLES.CREW, ROLES.LOAN_PILOT];
export const isRosterCrewRole = (role) => ROSTER_CREW_ROLES.includes(role);

/** Roster crew or external pilot — may be assigned to sectors / use crew inbox */
export const FLIGHT_CREW_ROLES = [ROLES.CREW, ROLES.LOAN_PILOT, ROLES.EXTERNAL_PILOT];
export const isFlightCrewRole = (role) => FLIGHT_CREW_ROLES.includes(role);

// Role display names (for UI)
export const ROLE_DISPLAY_NAMES = {
  [ROLES.SUPER_ADMIN]: 'Super Administrator',
  [ROLES.SPECTATOR]: 'Spectator',
  [ROLES.ACCOUNTABLE_MANAGER]: 'Accountable Manager',
  [ROLES.OPERATIONS_MANAGER]: 'Operations Manager',
  [ROLES.OPERATIONS_EXECUTIVE]: 'Operations Executive',
  [ROLES.SALES]: 'Sales',
  [ROLES.PLANNING_MANAGER]: 'Planning Manager',
  [ROLES.PLANNING_OFFICER]: 'Planning Officer',
  [ROLES.CREW]: 'Crew (Pilot)',
  [ROLES.LOAN_PILOT]: 'Loan Pilot',
  [ROLES.EXTERNAL_PILOT]: 'External Pilot',
  // Engineering & Maintenance (titles aligned with SCOPE-Role-Structure.md)
  [ROLES.CA_MANAGER]: 'CA Manager',
  [ROLES.CA_TECHNICAL_OFFICER]: 'CA Technical Officer',
  [ROLES.CAMO_QM_MANAGER]: 'CAMO QM Manager',
  [ROLES.CAMO_QM_OFFICER]: 'CAMO QM Officer',
  [ROLES.MRO_MANAGER]: 'MRO Manager',
  [ROLES.MRO_ENGINEER]: 'MRO Engineer',
  [ROLES.MRO_QM_MANAGER]: 'MRO QM Manager',
  [ROLES.MRO_QM_OFFICER]: 'MRO QM Officer',
  [ROLES.AM_MRO]: 'AM MRO',
  [ROLES.STORE_INSPECTOR]: 'Store Inspector',
  [ROLES.STORE_EXECUTIVE]: 'Store Executive',
  [ROLES.SAFETY_COFS]: 'Chief of Flight Safety (COFS)',
  [ROLES.SAFETY_MANAGER]: 'Safety Manager (SMS)',
  [ROLES.SAFETY_OFFICER]: 'Safety Officer',
  [ROLES.MIS]: 'MIS',
  [ROLES.HR_MANAGER]: 'HR Manager',
  [ROLES.EMPLOYEE]: 'Employee',
  [ROLES.CLIENT]: 'Client',
  [ROLES.EXTERNAL_OPERATOR]: 'External Operator'
};

// Role color mapping (for chips, badges)
export const ROLE_COLORS = {
  [ROLES.SUPER_ADMIN]: 'error',
  [ROLES.SPECTATOR]: 'default',
  [ROLES.ACCOUNTABLE_MANAGER]: 'primary',
  [ROLES.OPERATIONS_MANAGER]: 'info',
  [ROLES.OPERATIONS_EXECUTIVE]: 'info',
  [ROLES.SALES]: 'secondary',
  [ROLES.PLANNING_MANAGER]: 'info',
  [ROLES.PLANNING_OFFICER]: 'info',
  [ROLES.CREW]: 'success',
  [ROLES.LOAN_PILOT]: 'success',
  [ROLES.EXTERNAL_PILOT]: 'success',
  [ROLES.CA_MANAGER]: 'warning',
  [ROLES.CA_TECHNICAL_OFFICER]: 'warning',
  [ROLES.CAMO_QM_MANAGER]: 'warning',
  [ROLES.CAMO_QM_OFFICER]: 'warning',
  [ROLES.MRO_MANAGER]: 'secondary',
  [ROLES.MRO_ENGINEER]: 'secondary',
  [ROLES.MRO_QM_MANAGER]: 'secondary',
  [ROLES.MRO_QM_OFFICER]: 'secondary',
  [ROLES.AM_MRO]: 'primary',
  [ROLES.STORE_INSPECTOR]: 'default',
  [ROLES.STORE_EXECUTIVE]: 'default',
  [ROLES.SAFETY_COFS]: 'warning',
  [ROLES.SAFETY_MANAGER]: 'warning',
  [ROLES.SAFETY_OFFICER]: 'warning',
  [ROLES.MIS]: 'default',
  [ROLES.HR_MANAGER]: 'primary',
  [ROLES.EMPLOYEE]: 'default',
  [ROLES.CLIENT]: 'primary',
  [ROLES.EXTERNAL_OPERATOR]: 'secondary'
};

